import type { CaseStudy } from '../types';

export const asianCrisis: CaseStudy = {
  id: 'asian-crisis',
  order: 15,
  title: '1997 亚洲金融危机',
  english: 'The Asian Financial Crisis',
  category: 'economics',
  era: '1997 — 1999',
  region: '东南亚 / 东亚',
  accent: '#a7782f',
  hook: '固定汇率、短期美元债和本币资产之间，藏着一个一旦信心反转就会断裂的期限与货币错配。',
  question: '为什么几个原本高速增长的经济体，会在几周内同时陷入货币贬值、银行挤兑和资本外逃？',
  summary:
    '1997 年泰铢贬值后，危机迅速扩散到印尼、韩国等地。本币资产与美元负债的错配、银行与企业的关联贷款、资本账户开放与固定汇率承诺，共同把一次汇率调整放大成金融与实体危机。',
  thesis: '危机的触发器可能是一个汇率数字，但真正的脆弱性在于：谁借了短期美元，谁持有不能立刻变现的本币资产。',
  quote: '固定汇率不是免费的稳定器；当承诺与外汇储备不匹配，它会把调整推迟到最痛的时刻。',
  isNew: true,
  entryNodeIds: ['asia-peg', 'asia-carry', 'asia-banking'],
  focusNodeId: 'asia-carry',
  lensIds: ['mismatch', 'reflexivity', 'regulation', 'commitment'],
  opportunityIds: ['opp-fx-risk'],
  sourceIds: ['fed-asian-crisis', 'worldbank-east-asia'],
  facts: [
    {
      text: '1997 年 7 月 2 日，泰国放弃泰铢与美元的事实挂钩，泰铢随后大幅贬值；危机扩散至印尼、韩国、马来西亚等地。',
      sourceIds: ['fed-asian-crisis'],
    },
    {
      text: '危机前，部分东亚经济体存在短期外债上升、银行信贷快速扩张和本币资产价格繁荣；具体债务与储备比例因国家而异。',
      sourceIds: ['fed-asian-crisis'],
    },
    {
      text: 'IMF 等机构提供了大规模救助，并附加财政、金融与结构改革条件；紧缩与救助的先后顺序至今仍有争论。',
      sourceIds: ['fed-asian-crisis', 'worldbank-east-asia'],
    },
  ],
  nodes: [
    {
      id: 'asiancrisis',
      caseId: 'asian-crisis',
      kind: 'case',
      stage: 0,
      lane: 0,
      title: '1997 亚洲金融危机',
      summary: '从泰铢贬值扩散为区域性的货币、银行与增长危机。',
      detail:
        '1990 年代，东亚多国以高增长、高储蓄和出口导向著称。资本流入推高资产价格，银行与企业借入短期美元债务，本币却与美元保持相对稳定。当出口放缓和信心转向，固定汇率承诺变得难以维持，资本外流与银行挤兑互相放大。',
      boundary: '不同国家的危机成因与政策反应差异很大；不能把“亚洲模式”作为一个单一原因。',
      evidence: 'fact',
      confidence: 'high',
      sourceIds: ['fed-asian-crisis'],
    },
    {
      id: 'asia-peg',
      caseId: 'asian-crisis',
      kind: 'mechanism',
      stage: 1,
      lane: -1,
      title: '汇率承诺：稳定预期，也积累错配',
      summary: '本币与美元挂钩降低了贸易与借贷的不确定性，却让贬值风险被低估。',
      detail:
        '固定或管理浮动汇率让企业觉得借美元、收本币是安全的：汇率稳定，美元利率又低。但承诺越久，未对冲的美元负债越多。当储备不足以继续干预，汇率调整会一次性释放，资产负债表瞬间恶化。',
      boundary: '固定汇率本身不必然导致危机；关键在于外债、储备、银行监管和资本流动管理。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['fed-asian-crisis'],
    },
    {
      id: 'asia-carry',
      caseId: 'asian-crisis',
      kind: 'mechanism',
      stage: 1,
      lane: 1,
      title: '货币错配：借美元，持有本币资产',
      summary: '短期美元负债与本币收入/资产不匹配，贬值会同时抬高债务和压低资产。',
      detail:
        '企业与银行借入低息美元，投资于本币计价的房地产、工厂或本地贷款。平时，利差与增长让杠杆看起来划算；贬值时，美元债务换算成本币大幅上升，而本币资产价值不变甚至下跌。净资产被双向挤压。',
      boundary: '如果债务有自然对冲（美元收入）、期限较长或有外汇衍生品保护，货币错配的伤害会小得多。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['fed-asian-crisis'],
    },
    {
      id: 'asia-banking',
      caseId: 'asian-crisis',
      kind: 'mechanism',
      stage: 2,
      lane: -1,
      title: '银行关联贷款：增长掩盖坏账',
      summary: '银行与关系企业、政府项目紧密相连，风险集中且缺少独立审查。',
      detail:
        '在高速增长期，房地产与制造业贷款看起来安全，银行倾向于向关联企业放贷。监管资本、贷款分类与信息披露不足，使坏账被增长掩盖。当汇率与资产价格转向，坏账同时暴露，银行收缩信贷。',
      boundary: '关联贷款不是亚洲独有；治理质量、监管独立性与透明度决定风险程度。',
      evidence: 'inference',
      confidence: 'high',
      sourceIds: ['fed-asian-crisis', 'worldbank-east-asia'],
    },
    {
      id: 'asia-contagion',
      caseId: 'asian-crisis',
      kind: 'mechanism',
      stage: 2,
      lane: 1,
      title: '传染：共同债权人同时撤退',
      summary: '国际银行与基金持有相似敞口，一处违约会触发对整个区域的去杠杆。',
      detail:
        '当泰国出现问题，国际贷款人会重新评估印尼、韩国等相似市场，要求偿还或提高风险溢价。共同债权人、共同贸易伙伴和相似资产负债表让危机跨境传播，储备与汇率承诺逐一受到考验。',
      boundary: '传染程度取决于资本流动结构、区域贸易联系、国际救助速度与投资者信心。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['fed-asian-crisis'],
    },
    {
      id: 'asia-condition',
      caseId: 'asian-crisis',
      kind: 'condition',
      stage: 2,
      lane: 0,
      title: '前提：资本流入、储备与外债期限',
      summary: '大量短期资本流入、储备相对不足、外债期限短，让汇率承诺难以承受冲击。',
      detail:
        '资本账户开放让资金可以迅速进出，短期外债让债权人能快速撤出，而外汇储备是有限的。当出口放缓、资产价格高企，投资者开始怀疑汇率能否维持，承诺就进入自我否定的循环。',
      boundary: '资本管制、宏观审慎政策与更高储备可以改变冲击路径，但也会带来其他成本。',
      evidence: 'inference',
      confidence: 'high',
      sourceIds: ['fed-asian-crisis', 'worldbank-east-asia'],
    },
    {
      id: 'asia-outcome',
      caseId: 'asian-crisis',
      kind: 'outcome',
      stage: 3,
      lane: 0,
      title: '结果：贬值、救助与深度衰退',
      summary: '货币贬值、银行重组与 IMF 救助同时发生，增长与就业受到重创。',
      detail:
        '危机国家经历货币大幅贬值、利率上升、银行倒闭或重组、企业破产与失业上升。IMF 救助提供外汇，但附加的紧缩与结构改革引发激烈争论：救助是稳定了系统，还是把调整成本压给了普通民众？',
      boundary: '各国复苏速度不同；韩国等国在重组后较快恢复，印尼的政治与社会冲击更严重。',
      evidence: 'fact',
      confidence: 'high',
      sourceIds: ['fed-asian-crisis', 'worldbank-east-asia'],
    },
    {
      id: 'asia-risk',
      caseId: 'asian-crisis',
      kind: 'risk',
      stage: 4,
      lane: -1,
      title: '脆弱处：信心与储备的赛跑',
      summary: '固定汇率承诺一旦被怀疑，储备消耗会加速，资本外流可能自我实现。',
      detail:
        '投资者知道储备有限，因此更早撤离；撤离又消耗储备，进一步证明承诺不可信。这种反身性让“基本面尚可”的国家也可能被卷入。危机不是单纯由坏账触发，而是由对承诺可持续性的怀疑触发。',
      boundary: '充足储备、可信的退出安排、资本管制与外部支持可以打断这个循环。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['fed-asian-crisis'],
    },
    {
      id: 'asia-opp',
      caseId: 'asian-crisis',
      kind: 'opportunity',
      stage: 4,
      lane: 1,
      title: '机会透镜：帮小企业看见汇率风险',
      summary: '跨境小企业往往没有专职财务团队，汇率错配和账期风险容易被低估。',
      detail:
        '机会包括：为进出口小企业做外汇敞口盘点、报价币种与账期设计、简单对冲方案、压力测试与合同条款审查。不是预测汇率，而是让企业在不利变动下仍能活下来。',
      boundary: '衍生品与外汇产品受牌照和适当性监管；小企业付费能力有限，需要标准化、低门槛的服务。',
      evidence: 'inference',
      confidence: 'medium',
      sourceIds: ['fed-asian-crisis'],
    },
  ],
  edges: [
    {
      id: 'asia-e1',
      caseId: 'asian-crisis',
      from: 'asiancrisis',
      to: 'asia-peg',
      label: '汇率承诺',
      explanation: '本币与美元保持稳定，降低了贸易与借贷的汇率不确定性。',
      tone: 'cause',
    },
    {
      id: 'asia-e2',
      caseId: 'asian-crisis',
      from: 'asiancrisis',
      to: 'asia-carry',
      label: '低息美元诱惑',
      explanation: '美元利率较低、汇率看似稳定，企业倾向于借美元、投本币资产。',
      tone: 'cause',
    },
    {
      id: 'asia-e3',
      caseId: 'asian-crisis',
      from: 'asia-peg',
      to: 'asia-carry',
      label: '稳定幻觉',
      explanation: '汇率稳定让借款人低估贬值风险，减少了对冲。',
      tone: 'enable',
    },
    {
      id: 'asia-e4',
      caseId: 'asian-crisis',
      from: 'asia-carry',
      to: 'asia-banking',
      label: '杠杆进入银行体系',
      explanation: '银行借短美元、贷本币，资产负债表与房地产、制造业深度绑定。',
      tone: 'cause',
    },
    {
      id: 'asia-e5',
      caseId: 'asian-crisis',
      from: 'asia-banking',
      to: 'asia-contagion',
      label: '坏账与共同债权人',
      explanation: '银行坏账暴露后，国际债权人重新评估整个区域的风险。',
      tone: 'cause',
    },
    {
      id: 'asia-e6',
      caseId: 'asian-crisis',
      from: 'asia-carry',
      to: 'asia-outcome',
      label: '贬值击穿资产负债表',
      explanation: '本币贬值让美元债务变贵，资产价格下跌又压缩抵押品价值。',
      tone: 'cause',
    },
    {
      id: 'asia-e7',
      caseId: 'asian-crisis',
      from: 'asia-contagion',
      to: 'asia-outcome',
      label: '资本外流与救助',
      explanation: '区域资本外流迫使各国提高利率、寻求 IMF 救助并重组银行。',
      tone: 'cause',
    },
    {
      id: 'asia-e8',
      caseId: 'asian-crisis',
      from: 'asia-condition',
      to: 'asia-outcome',
      label: '期限与储备前提',
      explanation: '短期外债、有限储备和开放资本账户，使汇率承诺难以承受冲击。',
      tone: 'enable',
    },
    {
      id: 'asia-e9',
      caseId: 'asian-crisis',
      from: 'asia-outcome',
      to: 'asia-risk',
      label: '信心自我实现',
      explanation: '救助与贬值让投资者更担心邻国，资本外流与储备消耗互相强化。',
      tone: 'risk',
    },
    {
      id: 'asia-e10',
      caseId: 'asian-crisis',
      from: 'asia-risk',
      to: 'asia-opp',
      label: '风险管理需求',
      explanation: '汇率与期限风险暴露后，小企业更愿意为简单、可执行的风险管理付费。',
      tone: 'enable',
    },
  ],
};
